import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  Menu, X, LayoutDashboard, History,
  Gauge, BookOpen, Grid, ListChecks, User, Clock, ClipboardList,
} from 'lucide-react';
import { useTrainingLang } from './i18n/TrainingLangContext';

const TrainingTileSidebar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { currentUser } = useAuth();
  const { t } = useTrainingLang();
  const inTrainingKiosk = !!currentUser?.trainingKioskUser;
  // In training-kiosk mode, hide Administration entirely (per product decision) — admins use the
  // normal login if they need those tools.
  const isAdmin = !inTrainingKiosk && currentUser?.roles?.some((r) => r.id === 1 || r.name === 'Admin');

  const sections = [
    {
      category: t('navGeneral'),
      items: [
        { name: t('navDashboard'), path: '/tiles/ksp_training/dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
        { name: t('navSurveys'), path: '/tiles/ksp_training/surveys', icon: <ClipboardList className="w-5 h-5" /> },
        { name: t('navHistory'), path: '/tiles/ksp_training/history', icon: <History className="w-5 h-5" /> },
      ],
    },
  ];

  if (isAdmin) {
    // Administration stays in English: these screens are used by HQ staff and their
    // labels match the reports and CSV headers they hand around.
    sections.push({
      category: 'Administration',
      items: [
        { name: 'Metrics Hub', path: '/tiles/ksp_training/admin/hub', icon: <Gauge className="w-5 h-5" /> },
        { name: 'Trainings', path: '/tiles/ksp_training/admin/trainings', icon: <BookOpen className="w-5 h-5" /> },
        { name: 'Completion Matrix', path: '/tiles/ksp_training/admin/matrix', icon: <Grid className="w-5 h-5" /> },
        { name: 'By Training', path: '/tiles/ksp_training/admin/by-training', icon: <ListChecks className="w-5 h-5" /> },
        { name: 'By User', path: '/tiles/ksp_training/admin/by-user', icon: <User className="w-5 h-5" /> },
        { name: 'Surveys', path: '/tiles/ksp_training/admin/surveys', icon: <ClipboardList className="w-5 h-5" /> },
      ],
    });
  }

  const handleBackToClockIn = () => {
    const adminToken = localStorage.getItem('trainingKioskAdminToken');
    const adminUser = localStorage.getItem('trainingKioskAdminUser');
    if (adminToken && adminUser) {
      localStorage.setItem('token', adminToken);
      localStorage.setItem('user', adminUser);
      // Preselect this trainee on the unified kiosk's confirm screen.
      if (currentUser?.id) {
        sessionStorage.setItem('kioskReturnUserId', String(currentUser.id));
      }
      window.location.href = '/tiles/labor_management_system/kiosk/kiosk-view';
    } else {
      // Defensive fallback: if the admin token was lost somehow, clear out and head back to login.
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <>
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-slate-800 text-slate-200"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div
        className={`fixed inset-y-0 left-0 z-40 transition-transform duration-300 transform
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          h-screen bg-slate-800 border-r border-slate-700 w-64 overflow-y-auto`}
      >
        <div className="p-4 border-b border-slate-700/50">
          {inTrainingKiosk ? (
            <button
              type="button"
              onClick={handleBackToClockIn}
              className="w-full flex items-center px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-md transition-colors"
            >
              <Clock className="w-4 h-4 mr-3" />
              <span>{t('backToClockIn')}</span>
            </button>
          ) : (
            <Link
              to="/"
              className="flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-slate-700/50 hover:text-slate-100 rounded-md"
            >
              <span className="mr-3">←</span>
              <span>{t('backToHQ')}</span>
            </Link>
          )}
        </div>

        <nav className="mt-4">
          {sections.map((section) => (
            <div key={section.category} className="mb-6">
              <h3 className="px-4 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                {section.category}
              </h3>
              <div>
                {section.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center px-4 py-2 text-sm ${
                      isActive(item.path)
                        ? 'bg-amber-600 text-white'
                        : 'text-slate-300 hover:bg-slate-700/50 hover:text-slate-100'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="mr-3">{item.icon}</span>
                    <span>{item.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </div>
    </>
  );
};

export default TrainingTileSidebar;
